import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { CommonCardPrice } from '@sorare/core/src/components/collections/CommonCardPrice';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { commonMarketplace } from '@sorare/core/src/lib/glossary';

import { BuyCommonBenchObjectCongratsScreen } from '../BuyCommonBenchObjectCongratsScreen';
import { CommonCardSummary } from '../CommonCardSummary';
import { CommonPaymentBox, CommonPaymentBoxMode } from '../CommonPaymentBox';
import { BuyCommonCard_composeTeamBenchObjectInterface } from './__generated__/index.graphql';

type Props = {
  showPlayerCostHighlight?: boolean;
  commonPlayer: BuyCommonCard_composeTeamBenchObjectInterface & {
    __typename: 'ComposeTeamBenchCommonPlayer';
  };
};

const Root = styled(Vertical)`
  width: 100%;
`;

const Frame = styled(Horizontal)`
  width: 100%;
  padding: var(--double-unit);
  background-color: rgba(var(--c-rgb-white), 0.07);
  border-radius: var(--unit);
`;

export const BuyCommonCard = ({
  commonPlayer,
  showPlayerCostHighlight,
}: Props) => {
  const [paymentStarted, setPaymentStarted] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const { signedAddCardPrice } = commonPlayer.anyPlayer;

  return (
    <Root>
      <OnboardingHighlightWrapper
        highlightName="BuyCommonCard"
        wrap={showPlayerCostHighlight}
      >
        <Horizontal>
          <CommonCardPrice price={signedAddCardPrice?.price} />
        </Horizontal>
      </OnboardingHighlightWrapper>
      <Button
        fullWidth
        size="small"
        color="tertiary"
        onClick={() => setPaymentStarted(true)}
        disabled={!signedAddCardPrice}
      >
        <FormattedMessage
          {...(signedAddCardPrice
            ? commonMarketplace.add
            : commonMarketplace.addNotAvailable)}
        />
      </Button>
      {signedAddCardPrice && (
        <CommonPaymentBox
          open={paymentStarted}
          slug={commonPlayer.anyPlayer.slug}
          onClose={() => setPaymentStarted(false)}
          subtotal={signedAddCardPrice.price}
          total={signedAddCardPrice.price}
          fees={0}
          signedPrice={signedAddCardPrice.signedPrice}
          mode={CommonPaymentBoxMode.ADD}
          commonCardSummaryNode={
            <Frame>
              <CommonCardSummary commonPlayer={commonPlayer} />
            </Frame>
          }
          onSuccess={() => {
            setPaymentStarted(false);
            setPaymentSuccess(true);
          }}
        />
      )}
      <BuyCommonBenchObjectCongratsScreen
        open={paymentSuccess}
        onClose={() => setPaymentSuccess(false)}
        commonPlayer={commonPlayer}
      />
    </Root>
  );
};

BuyCommonCard.fragments = {
  composeTeamBenchObjectInterface: gql`
    fragment BuyCommonCard_composeTeamBenchObjectInterface on ComposeTeamBenchObjectInterface {
      id
      anyPlayer {
        slug
        sport
        cardPrice(rarity: common)
        signedAddCardPrice(rarity: common) {
          price
          signedPrice
        }
      }
      ...CommonCardSummary_composeTeamBenchObjectInterface
      ...BuyCommonBenchObjectCongratsScreen_composeTeamBenchObjectInterface
    }
    ${CommonCardSummary.fragments.composeTeamBenchObjectInterface}
    ${BuyCommonBenchObjectCongratsScreen.fragments
      .composeTeamBenchObjectInterface}
  ` as TypedDocumentNode<BuyCommonCard_composeTeamBenchObjectInterface>,
};
